import type { AnyID, SceneObjects, Store, Types } from '@types'
import { Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import type { HistorySnapshot } from './types'

function toSet(values: unknown): Set<string> {
  if (values instanceof Set) return new Set(values)
  if (Array.isArray(values)) return new Set(values.map(String))
  return new Set()
}

function toMap<T>(values: unknown): Map<string, T> {
  if (values instanceof Map) return new Map(values)
  if (values && typeof values === 'object') {
    return new Map(Object.entries(values as Record<string, T>))
  }
  return new Map()
}

export function rehydrateLayers(layers: Store['layers']): Store['layers'] {
  return layers.map((layer) => ({
    ...layer,
    items: toSet(layer.items),
    areas: toSet(layer.areas),
    selected: toSet(layer.selected),
    deltas: toMap(layer.deltas),
  })) as Store['layers']
}

function rehydrateMatrix4(value: unknown): Matrix4 | undefined {
  if (value instanceof Matrix4) return value.clone()
  if (!value || typeof value !== 'object') return undefined
  const candidate = value as { elements?: number[] }
  if (!Array.isArray(candidate.elements) || candidate.elements.length !== 16) return undefined
  return new Matrix4().fromArray(candidate.elements)
}

function rehydrateVector3(value: unknown): Vector3 | undefined {
  if (value instanceof Vector3) return value.clone()
  if (!value || typeof value !== 'object') return undefined
  const candidate = value as { x?: number; y?: number; z?: number }
  if (
    typeof candidate.x !== 'number' ||
    typeof candidate.y !== 'number' ||
    typeof candidate.z !== 'number'
  ) {
    return undefined
  }
  return new Vector3(candidate.x, candidate.y, candidate.z)
}

function rehydrateMatrix3(value: unknown): Matrix3 | undefined {
  if (value instanceof Matrix3) return value.clone()
  if (!value || typeof value !== 'object') return undefined
  const candidate = value as { elements?: number[] }
  if (!Array.isArray(candidate.elements) || candidate.elements.length !== 9) return undefined
  return new Matrix3().fromArray(candidate.elements)
}

function rehydrateOBB(value: unknown): OBB | undefined {
  if (value instanceof OBB) return value.clone()
  if (!value || typeof value !== 'object') return undefined
  const candidate = value as {
    center?: unknown
    halfSize?: unknown
    rotation?: unknown
  }
  const center = rehydrateVector3(candidate.center)
  const halfSize = rehydrateVector3(candidate.halfSize)
  if (!center || !halfSize) return undefined
  const rotation = rehydrateMatrix3(candidate.rotation)
  return rotation ? new OBB(center, halfSize, rotation) : new OBB(center, halfSize)
}

function rehydrateSceneItem(item: SceneObjects): SceneObjects {
  const next = { ...item } as SceneObjects
  const matrix = rehydrateMatrix4((item as { matrix?: unknown }).matrix)
  if (matrix) {
    ;(next as { matrix: Matrix4 }).matrix = matrix
  }
  const rotation = (item as { rotation?: unknown }).rotation
  if (rotation && typeof rotation === 'object' && 'isEuler' in (rotation as object)) {
    ;(next as { rotation: typeof rotation }).rotation = (
      rotation as import('three').Euler
    ).clone()
  }
  const quaternion = (item as { quaternion?: unknown }).quaternion
  if (quaternion) {
    const hydrated = rehydrateVector3(quaternion)
    if (hydrated) {
      ;(next as { quaternion: Quaternion }).quaternion = new Quaternion(
        hydrated.x,
        hydrated.y,
        hydrated.z,
        (quaternion as { w?: number }).w ?? 1,
      )
    }
  }
  const childs = (item as { childs?: unknown }).childs
  if (childs !== undefined) {
    ;(next as { childs?: Set<AnyID> }).childs = toSet(childs) as Set<AnyID>
  }
  return next
}

export function rehydrateHistorySnapshot(snapshot: HistorySnapshot): HistorySnapshot {
  const items = new Map<AnyID, SceneObjects>()
  snapshot.items.forEach((item, id) => {
    items.set(id, rehydrateSceneItem(item))
  })

  const sceneBoundingBoxes = new Map<string, { type: Types; boundingBox: OBB }>()
  snapshot.sceneBoundingBoxes.forEach((entry, key) => {
    const boundingBox = rehydrateOBB(entry.boundingBox)
    if (!boundingBox) return
    sceneBoundingBoxes.set(key, { type: entry.type, boundingBox })
  })

  return {
    items,
    areas: new Map(snapshot.areas),
    layers: rehydrateLayers(snapshot.layers),
    sceneBoundingBoxes,
    collisionableItems: new Set(snapshot.collisionableItems),
    price: snapshot.price,
  }
}
